import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators} from '@angular/forms';
import {MatOptionSelectionChange} from '@angular/material/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil, tap} from 'rxjs/operators';
import {Response} from '../../models';
import {AuthenticationService, MapsAPILoader, MemberService, SnackbarService} from '../../services';
import {ALLOWED_PROFILE_LOGO_FILE_TYPES, EMAIL_REGEX, MAX_FILE_SIZE_BYTES} from '../../helpers';

declare var google: any;

@Component({
    selector: 'prism-personal-information',
    templateUrl: './personal-information.component.html',
    styleUrls: ['./personal-information.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class PersonalInformationComponent implements OnInit, OnDestroy {
    loadingMemberData = true;
    freshUntouchedProfileData: { firstName: string; lastName: string; loginEmail: string; secondaryEmail: string; contactNumber: string; postalAddress: string; };
    autocompleteService: any;
    addressAutoCompleteOptions = new BehaviorSubject<string[]>([]);
    // The postal address must exactly match an address chosen from the Google Places suggestions
    lastAutoSelectionMade: string;
    isContractor: boolean;
    companyImageUrl: string | null = null;
    logoPreviewUrl: string | ArrayBuffer | null = null;
    logoUploading = false;
    @ViewChild('logoInput') logoInputRef: ElementRef<HTMLInputElement>;
    @Input() showCompanyFields: boolean = true;
    addressSelectedValidator = (control: AbstractControl): ValidationErrors | null => {
        if (!control.value || control.value === this.lastAutoSelectionMade) {
            return null;
        }
        return {addressNotSelected: true};
    };
    optionalWebsiteValidator = (control: AbstractControl): ValidationErrors | null => {
        const value = (control.value || '').trim();
        if (!value) {
            return null;
        }
        return /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([\/?#].*)?$/i.test(value)
            ? null
            : {invalidWebsite: true};
    };
    personalInformationForm: FormGroup = this.formBuilder.group({
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        loginEmail: new FormControl('', [Validators.required]),
        secondaryEmail: new FormControl('', [Validators.required, Validators.pattern(EMAIL_REGEX)]),
        contactNumber: new FormControl('', [Validators.required]),
        postalAddress: new FormControl('', [Validators.required, this.addressSelectedValidator]),
        companyName: new FormControl('', [Validators.required]),
        companyId: new FormControl('', [Validators.required]),
        companyWebsite: new FormControl('', [this.optionalWebsiteValidator]),
    });
    subDestroyer$: Subject<void> = new Subject<void>();

    constructor(private formBuilder: FormBuilder,
                private memberService: MemberService,
                private snackbarService: SnackbarService,
                private mapsAPILoader: MapsAPILoader,
                private authService: AuthenticationService,
                private cd: ChangeDetectorRef) {
    }

    ngOnInit() {
        this.fetchAndSetMemberProfileData();

        this.mapsAPILoader.load()
            .pipe(takeUntil(this.subDestroyer$))
            .subscribe(() => {
                this.autocompleteService = new google.maps.places.AutocompleteService();

                this.postalAddressControl.valueChanges.pipe(debounceTime(100), distinctUntilChanged(), takeUntil(this.subDestroyer$))
                    .subscribe(val => {
                        if (val && val !== this.lastAutoSelectionMade) {
                            this.autocompleteService.getPlacePredictions({
                                types: ['address'],
                                input: val
                            }, this.handleAddressAutocompleteResponse);
                        } else if (!val) {
                            this.addressAutoCompleteOptions.next([]);
                        }
                    });
            });
    }

    ngOnDestroy() {
        this.subDestroyer$.next();
    }

    @Output('personalInformationSaved') personalInfoSaved: EventEmitter<void> = new EventEmitter<void>();

    get firstNameControl() {
        return this.personalInformationForm.get('firstName');
    }

    get lastNameControl() {
        return this.personalInformationForm.get('lastName');
    }

    get loginEmailControl() {
        return this.personalInformationForm.get('loginEmail');
    }

    get secondaryEmailControl() {
        return this.personalInformationForm.get('secondaryEmail');
    }

    get contactNumberControl() {
        return this.personalInformationForm.get('contactNumber');
    }

    get postalAddressControl() {
        return this.personalInformationForm.get('postalAddress');
    }

    get companyNameControl() {
        return this.personalInformationForm.get('companyName');
    }

    get companyIdControl() {
        return this.personalInformationForm.get('companyId');
    }

    get companyWebsiteControl() {
        return this.personalInformationForm.get('companyWebsite');
    }

    get personalRawFormData() {
        return this.personalInformationForm.getRawValue();
    }

    getMemberData(updateData?) {
        return this.memberService.getMember(updateData)
            .pipe(
                tap(({data}: Response) => {
                    this.populateFields(data);
                    this.loadingMemberData = false;
                })
            );
    }

    saveAccount() {
        this.personalInformationForm.disable();
        this.loadingMemberData = true;
        this.memberService.updateMember(this.personalRawFormData)
            .toPromise()
            .then(({data: memberProfileData}: Response) => {
                this.snackbarService.showSnackbar('Your profile has been updated!');
                this.fetchAndSetMemberProfileData(memberProfileData);
                this.personalInfoSaved.emit();
            })
            .catch(err => {
                console.error('ERROR UPDATING MEMBER:', err);
            });
    }

    populateFields(memberData) {
        const {
            firstName,
            lastName,
            loginEmail,
            secondaryEmail,
            contactNumber,
            postalAddress,
            companyName,
            companyId,
            companyWebsite,
            companyImageUrl
        } = memberData;
        this.freshUntouchedProfileData = {firstName, lastName, loginEmail, secondaryEmail, contactNumber, postalAddress};
        this.companyImageUrl = companyImageUrl;
        // A previously saved address came from the suggestions, so treat it as a valid selection
        this.lastAutoSelectionMade = postalAddress;
        this.loginEmailControl.enable();
        this.firstNameControl.setValue(firstName);
        this.lastNameControl.setValue(lastName);
        this.loginEmailControl.setValue(loginEmail);
        this.loginEmailControl.updateValueAndValidity();
        this.secondaryEmailControl.setValue(secondaryEmail);
        this.contactNumberControl.setValue(contactNumber);
        this.postalAddressControl.setValue(postalAddress);
        this.companyNameControl.setValue(companyName);
        this.companyIdControl.setValue(companyId);
        this.companyWebsiteControl.setValue(companyWebsite || '');
        this.personalInformationForm.updateValueAndValidity();
        this.loginEmailControl.disable();
        this.personalInformationForm.markAsPristine();
        this.loadingMemberData = false;
        this.cd.markForCheck();
    }

    fetchAndSetMemberProfileData(updateData?) {
        return this.getMemberData(updateData)
            .toPromise()
            .then((memberResponse: Response) => {
                const memberData = memberResponse.data;
                this.authService.updateMemberData(memberData);
                this.populateFields(memberData);
            })
            .catch(err => {
                console.error('Could not fetch member profile data:', err);
                this.snackbarService.handleError('There was an error fetching your information. Please refresh page.');
            });
    }

    triggerLogoUpload() {
        if (!this.logoUploading) {
            this.logoInputRef.nativeElement.click();
        }
    }

    onLogoFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files && input.files[0];
        input.value = '';

        if (!file) {
            return;
        }

        if (!ALLOWED_PROFILE_LOGO_FILE_TYPES.includes(file.type)) {
            this.snackbarService.handleError('Please select a PNG or JPEG image for your company logo');
            return;
        }

        if (file.size > MAX_FILE_SIZE_BYTES) {
            this.snackbarService.handleError('Company logo image must be smaller than 500KB');
            return;
        }

        this.uploadCompanyLogo(file);
    }

    uploadCompanyLogo(file: File) {
        this.logoUploading = true;
        this.cd.markForCheck();

        const reader = new FileReader();
        reader.onload = () => {
            this.logoPreviewUrl = reader.result;
            this.cd.markForCheck();
        };
        reader.readAsDataURL(file);

        this.memberService.getCompanyLogoUploadUrl({
            contentType: file.type,
            fileName: file.name,
            contentLength: file.size
        })
            .toPromise()
            .then(({data: uploadUrl}: Response) => {
                return this.memberService.uploadCompanyLogo(uploadUrl, file).toPromise();
            })
            .then(() => {
                this.snackbarService.showSnackbar('Your company logo has been updated!');
                return this.memberService.getMember().toPromise()
                    .then((response: any) => {
                        this.companyImageUrl = response.data.companyImageUrl;
                    });
            })
            .catch(err => {
                console.error('ERROR UPLOADING COMPANY LOGO:', err);
                this.snackbarService.handleError('Your company logo could not be uploaded');
            })
            .finally(() => {
                this.logoUploading = false;
                this.logoPreviewUrl = null;
                this.cd.markForCheck();
            });
    }

    handleAddressAutocompleteResponse = (placeList) => {
        if (placeList && Array.isArray(placeList) && placeList.length) {
            this.addressAutoCompleteOptions.next(placeList.map(place => place.description));
        } else {
            this.addressAutoCompleteOptions.next([]);
        }
        this.cd.markForCheck();
    };

    autoAddressSelectionMade(event: MatOptionSelectionChange) {
        if (event.isUserInput && event.source.selected) {
            this.lastAutoSelectionMade = event.source.value;
            this.postalAddressControl.updateValueAndValidity();
        }
    }
}
